/*
 * Mara League OS — Event Log (Phase 5 · §9)
 * -----------------------------------------
 * Live view of the immutable event log. Live-tailing via Server-Sent
 * Events plus paginated history query. Every state mutation in the system
 * fires a structured event visible here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, Play, Pause, RefreshCw, Filter, X, Building2, Zap, ChevronDown,
} from "lucide-react";

const CATEGORY_COLOR = {
  match: "text-red-400 border-red-800 bg-red-950/30",
  auction: "text-amber-400 border-amber-800 bg-amber-950/30",
  registration: "text-emerald-400 border-emerald-800 bg-emerald-950/30",
  tenant: "text-purple-400 border-purple-800 bg-purple-950/30",
  config: "text-sky-400 border-sky-800 bg-sky-950/30",
  user: "text-zinc-400 border-zinc-700 bg-zinc-800/30",
};

function categoryOf(eventType) {
  return (eventType || "").split(".")[0] || "other";
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso.slice(11, 19); }
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso.slice(0, 10); }
}

export default function EventLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tailing, setTailing] = useState(true);
  const [scope, setScope] = useState("current");   // current | all (super_admin)
  const [typeFilter, setTypeFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const esRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, tenant_scope: scope };
      if (typeFilter) params.event_type = typeFilter;
      const [list, st] = await Promise.all([
        api.get("/events", { params }),
        api.get("/events/stats", { params: { hours: 24 } }),
      ]);
      setItems(list.data.items);
      setTotal(list.data.total);
      setStats(st.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [scope, typeFilter]);

  useEffect(() => { load(); }, [load]);

  // Server-Sent Events live tail
  useEffect(() => {
    if (!tailing) {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      return;
    }
    // Use fetch with streaming since EventSource can't send Auth headers.
    // Fall back to polling every 3s.
    const iv = setInterval(async () => {
      try {
        const params = { limit: 20, tenant_scope: scope };
        if (typeFilter) params.event_type = typeFilter;
        const { data } = await api.get("/events", { params });
        setItems((prev) => {
          const existingIds = new Set(prev.map((e) => e.id));
          const fresh = data.items.filter((e) => !existingIds.has(e.id));
          if (!fresh.length) return prev;
          return [...fresh, ...prev].slice(0, 400);
        });
        setTotal(data.total);
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [tailing, scope, typeFilter]);

  const filtered = items;
  const canScopeAll = user?.role === "super_admin";
  const maxCount = useMemo(
    () => Math.max(1, ...(stats?.types || []).map((t) => t.count)),
    [stats]
  );

  if (!user || (user.role !== "super_admin" && user.role !== "league_admin")) {
    return <div className="p-8 text-zinc-400">Super Admin or League Admin only.</div>;
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            §9 · Decoupled Event-Sourced Data Flow
          </div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">
            Event Log
          </h1>
          <div className="text-xs text-zinc-400 mt-1">
            Immutable log of every state mutation. Live-tail via server stream · filter
            · replay history.
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {canScopeAll && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="bg-[#060d09] border-white/10 text-white w-44 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a1611] border-white/10 text-white">
                <SelectItem value="current">This tenant only</SelectItem>
                <SelectItem value="all">All tenants (platform)</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={() => setTailing((t) => !t)}
            className={tailing ? "border-emerald-800 text-emerald-400" : "border-white/10 text-zinc-300"}
            data-testid="event-tail-toggle">
            {tailing ? <><Play className="w-4 h-4 mr-1.5 fill-current" /> Live-tail ON</>
                     : <><Pause className="w-4 h-4 mr-1.5" /> Paused</>}
          </Button>
          <Button variant="outline" onClick={load}
            className="border-white/10 text-zinc-300">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats histogram — last 24h */}
      {stats?.types?.length ? (
        <Card className="p-4 bg-[#11241b] border-white/10 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-emerald-400" />
            <div className="font-anton uppercase text-sm text-white">Last 24h · {stats.total_events} events</div>
            {stats.tenant_scope === "all" && (
              <Badge variant="outline" className="border-purple-800 text-purple-400 text-[10px]">PLATFORM</Badge>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {stats.types.slice(0, 12).map((t) => {
              const cat = categoryOf(t.event_type);
              const pct = (t.count / maxCount) * 100;
              const isActive = typeFilter === t.event_type;
              return (
                <button key={t.event_type}
                  onClick={() => setTypeFilter(isActive ? "" : t.event_type)}
                  className={`text-left p-2 rounded border transition-all ${
                    isActive ? "border-emerald-500 bg-emerald-950/40" : "border-white/5 bg-[#0d1c15] hover:border-white/20"
                  }`}
                  data-testid={`stat-${t.event_type}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`font-mono ${CATEGORY_COLOR[cat]?.split(" ")[0] || "text-white"}`}>{t.event_type}</span>
                    <span className="font-mono text-white">{t.count}</span>
                  </div>
                  <div className="w-full h-1 bg-black/40 rounded mt-1.5 overflow-hidden">
                    <div className="h-1 rounded" style={{ width: `${pct}%`, background: "currentColor" }} />
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      ) : null}

      {/* Filters */}
      <Card className="p-3 bg-[#11241b] border-white/10 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Filter by event type (e.g. match.* or auction.player_sold)"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-[#060d09] border-white/10 text-white font-mono text-xs max-w-md"
            data-testid="event-type-filter"
          />
          {typeFilter && (
            <Button size="sm" variant="outline" onClick={() => setTypeFilter("")}
              className="border-white/10 text-zinc-300 h-8">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
          <div className="ml-auto text-xs text-zinc-500">
            {filtered.length} shown · {total} total{typeFilter && ` matching "${typeFilter}"`}
          </div>
        </div>
      </Card>

      {/* Log stream */}
      <Card className="bg-[#11241b] border-white/10 overflow-hidden">
        {loading && !items.length ? (
          <div className="p-8 text-zinc-500 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-zinc-500 text-center text-sm">
            No events yet. Fire a match point, run an auction, or change a config to populate the log.
          </div>
        ) : (
          <div className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
            {filtered.map((e) => {
              const cat = categoryOf(e.event_type);
              const expanded = expandedId === e.id;
              const cls = CATEGORY_COLOR[cat] || "text-white border-white/10";
              return (
                <div key={e.id}
                  className="p-3 hover:bg-black/20 transition-colors"
                  data-testid={`event-row-${e.event_type}`}>
                  <button
                    className="w-full text-left flex items-start gap-3"
                    onClick={() => setExpandedId(expanded ? null : e.id)}>
                    <div className="text-[10px] text-zinc-500 font-mono min-w-[70px] pt-0.5">
                      {fmtTime(e.emitted_at)}
                    </div>
                    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-mono ${cls}`}>
                      <Zap className="w-2.5 h-2.5" />
                      {e.event_type}
                    </div>
                    <div className="flex-1 min-w-0 text-xs text-zinc-300">
                      {e.entity_type && <span className="text-zinc-500">{e.entity_type}:</span>}{" "}
                      {e.entity_id && (
                        <span className="font-mono text-zinc-400">{e.entity_id.slice(0, 8)}</span>
                      )}
                      <PayloadPreview payload={e.payload} />
                    </div>
                    {e.actor_role && (
                      <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px] shrink-0">
                        {e.actor_role}
                      </Badge>
                    )}
                    <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </button>
                  {expanded && (
                    <div className="mt-2 ml-[86px] rounded bg-black/60 border border-white/5 p-3">
                      <pre className="text-[11px] text-emerald-200 font-mono whitespace-pre-wrap break-all">
{JSON.stringify(e, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {stats?.runtime && (
        <div className="mt-4 text-[10px] text-zinc-600 flex items-center gap-3 flex-wrap">
          <Building2 className="w-3 h-3" />
          <span>Runtime · {Object.keys(stats.runtime.subscribers || {}).length} handler groups ·
            {" "}{stats.runtime.sse_connections} live SSE conn.</span>
          {stats.runtime.errors && Object.keys(stats.runtime.errors).length > 0 && (
            <span className="text-red-500">handler errors: {JSON.stringify(stats.runtime.errors)}</span>
          )}
        </div>
      )}
    </div>
  );
}

function PayloadPreview({ payload }) {
  if (!payload) return null;
  const bits = [];
  if (payload.score_a != null && payload.score_b != null) {
    bits.push(<span key="s" className="text-white font-mono">{payload.score_a}-{payload.score_b}</span>);
  }
  if (payload.court) bits.push(<span key="c" className="text-zinc-400">{payload.court}</span>);
  if (payload.player_name) bits.push(<span key="p" className="text-amber-400">{payload.player_name}</span>);
  if (payload.team_name) bits.push(<span key="t" className="text-emerald-400">{payload.team_name}</span>);
  if (payload.amount != null && payload.currency_symbol) {
    bits.push(<span key="a" className="text-amber-300 font-mono">{payload.currency_symbol} {Number(payload.amount).toLocaleString()}</span>);
  }
  if (payload.winner_name) bits.push(<span key="w" className="text-emerald-400">🏆 {payload.winner_name}</span>);
  if (payload.slug) bits.push(<span key="sl" className="text-purple-400 font-mono">/{payload.slug}</span>);
  if (payload.email) bits.push(<span key="e" className="text-zinc-300">{payload.email}</span>);
  if (payload.keys && Array.isArray(payload.keys)) {
    bits.push(<span key="k" className="text-sky-400">{payload.keys.slice(0, 3).join(", ")}{payload.keys.length > 3 ? "…" : ""}</span>);
  }
  if (!bits.length) return null;
  return <> · {bits.reduce((prev, curr, i) => [prev, <span key={`sep-${i}`} className="text-zinc-600 mx-1">·</span>, curr])}</>;
}
